import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatCurrency, formatDate, formatApiError as formatApiErrorLib } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Download, Pencil, Settings2, Mail, GripVertical, Eye } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAttachmentPreview } from "@/hooks/useAttachmentPreview";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";

const DEFAULT_LINES = [
  { description: "Diritti di emissione", amount: 35 },
  { description: "Corrispettivo per servizio di brokeraggio assicurativo", amount: 30 },
  { description: "Spese generali di gestione", amount: 35 },
  { description: "Premio assicurativo riscosso che viene versato alla Compagnia Assicuratrice", amount: 0 },
];

function formatApiError(e) {
  return formatApiErrorLib ? formatApiErrorLib(e) : (e?.response?.data?.detail || e?.message || "Errore imprevisto");
}

export default function Receipts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // 🆕 Filtro archivio per anno (default: anno corrente)
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const { openPreview: openAttachmentPreview, PreviewDialog: AttachmentPreviewDialog } = useAttachmentPreview();
  const [openEdit, setOpenEdit] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [editing, setEditing] = useState(null);
  // 🆕 Se il draft proviene da una polizza, dopo il save torniamo al dialog Modifica polizza
  const [returnToPolicyId, setReturnToPolicyId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year: yearFilter };
      if (q) params.q = q;
      const { data } = await api.get("/receipts", { params });
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  const loadYears = async () => {
    try {
      const { data } = await api.get("/receipts/years");
      const yrs = Array.isArray(data?.years) ? data.years : [];
      const currentYear = new Date().getFullYear();
      // Assicura che l'anno corrente sia sempre presente
      const merged = Array.from(new Set([currentYear, ...yrs])).sort((a, b) => b - a);
      setAvailableYears(merged);
    } catch { /* silent */ }
  };
  const loadClients = async () => {
    try {
      const { data } = await api.get("/clients", { params: { limit: 500 } });
      const arr = Array.isArray(data) ? data : data?.items || [];
      setClients(arr);
    } catch { /* silent */ }
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/agency/receipt-settings");
      setSettings(data);
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };
  useEffect(() => { load(); loadClients(); loadSettings(); loadYears(); }, []);
  // 🆕 Ricarica quando cambia l'anno di archivio
  useEffect(() => { load(); }, [yearFilter]);

  // 🆕 Gestione draft precompilato da polizza (?draft=1 + sessionStorage)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("draft") !== "1") return;
    const raw = sessionStorage.getItem("receipt_draft");
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      // 🆕 Marker return-to-policy (rimosso prima di passare a setEditing per non finire nel payload)
      if (d._return_to_policy_id) {
        setReturnToPolicyId(d._return_to_policy_id);
        delete d._return_to_policy_id;
      }
      loadSettings().then((s) => {
        setEditing({
          id: null,
          client_id: d.client_id || "",
          policy_id: d.policy_id || null,
          receipt_date: new Date().toISOString().slice(0, 10),
          lines: (d.lines || s?.default_lines || DEFAULT_LINES).map((l) => ({ ...l })),
          iva_rate: d.iva_rate ?? s?.default_iva_rate ?? 0,
          ritenuta_rate: d.ritenuta_rate ?? s?.default_ritenuta_rate ?? 0,
          iva_exempt_text: d.iva_exempt_text || s?.iva_exempt_text || "",
          notes: d.policy_number ? `Polizza n. ${d.policy_number}` : "",
        });
        setOpenEdit(true);
      });
    } catch { /* ignore */ }
    sessionStorage.removeItem("receipt_draft");
    // Rimuovi il parametro dalla URL senza reload
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const openNew = async () => {
    const s = settings || await loadSettings();
    setEditing({
      id: null,
      client_id: "",
      policy_id: null,
      receipt_date: new Date().toISOString().slice(0, 10),
      lines: (s?.default_lines || DEFAULT_LINES).map((l) => ({ ...l })),
      iva_rate: s?.default_iva_rate ?? 0,
      ritenuta_rate: s?.default_ritenuta_rate ?? 0,
      iva_exempt_text: s?.iva_exempt_text || "",
      notes: "",
    });
    setOpenEdit(true);
  };
  const openExisting = async (r) => {
    setEditing({
      id: r.id,
      client_id: r.client_id,
      policy_id: r.policy_id || null,
      receipt_date: r.receipt_date || new Date().toISOString().slice(0, 10),
      lines: (r.lines || []).map((l) => ({ description: l.description, amount: l.amount })),
      iva_rate: r.iva_rate ?? 0,
      ritenuta_rate: r.ritenuta_rate ?? 0,
      iva_exempt_text: r.iva_exempt_text || "",
      notes: r.notes || "",
      receipt_number: r.receipt_number,
    });
    setOpenEdit(true);
  };

  const totals = useMemo(() => {
    if (!editing) return { imponibile: 0, iva: 0, rit: 0, tot: 0 };
    const imp = (editing.lines || []).reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const iva = Math.round(imp * ((Number(editing.iva_rate) || 0) / 100) * 100) / 100;
    const rit = Math.round(imp * ((Number(editing.ritenuta_rate) || 0) / 100) * 100) / 100;
    return { imponibile: imp, iva, rit, tot: Math.round((imp + iva - rit) * 100) / 100 };
  }, [editing]);

  const save = async () => {
    if (!editing?.client_id) { toast.error("Seleziona il cliente"); return; }
    if (!editing.lines?.length) { toast.error("Aggiungi almeno una voce"); return; }
    try {
      const payload = {
        client_id: editing.client_id,
        policy_id: editing.policy_id || null,
        receipt_date: editing.receipt_date,
        lines: editing.lines.map((l) => ({ description: l.description || "", amount: Number(l.amount) || 0 })),
        iva_rate: Number(editing.iva_rate) || 0,
        ritenuta_rate: Number(editing.ritenuta_rate) || 0,
        iva_exempt_text: editing.iva_exempt_text || null,
        notes: editing.notes || null,
      };
      if (editing.id) {
        await api.put(`/receipts/${editing.id}`, payload);
        toast.success("Ricevuta aggiornata");
      } else {
        await api.post("/receipts", payload);
        toast.success(returnToPolicyId ? "Ricevuta emessa e allegata alla polizza" : "Ricevuta emessa");
      }
      setOpenEdit(false);
      setEditing(null);
      // 🆕 Se veniamo da una polizza, torna al dialog Modifica polizza (con focus + PDF già allegato dal backend)
      if (returnToPolicyId) {
        const pid = returnToPolicyId;
        setReturnToPolicyId(null);
        navigate(`/policies?focus=${pid}`);
        return;
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (r) => {
    try {
      const resp = await api.get(`/receipts/${r.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ricevuta_${(r.receipt_number || r.id).replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const annulla = async (r) => {
    if (!window.confirm(`Annullare la ricevuta ${r.receipt_number}?`)) return;
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success("Ricevuta annullata");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Invio via email con anteprima
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // { receipt, toEmail }
  const openSendEmail = (r) => {
    const client = clients.find((c) => c.id === r.client_id);
    const to = client?.email || "";
    if (!to) {
      const manual = window.prompt(`Il cliente non ha email registrata.\nInserisci l'indirizzo email destinatario:`);
      if (!manual || !manual.trim()) return;
      setEmailTarget({ receipt: r, toEmail: manual.trim() });
    } else {
      setEmailTarget({ receipt: r, toEmail: to });
    }
    if (shouldSkipEmailPreview("receipt_to_client")) {
      // salta anteprima
      doSendEmail(r, to);
      return;
    }
    setEmailPreviewOpen(true);
  };
  const doSendEmail = async (receipt, toEmail) => {
    try {
      await api.post(`/receipts/${receipt.id}/send-to-client`, { to_email: toEmail });
      toast.success(`Ricevuta ${receipt.receipt_number} inviata a ${toEmail}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const clientLabel = (id) => {
    const c = clients.find((x) => x.id === id);
    if (!c) return id;
    return `${c.surname || ""} ${c.name || ""}`.trim() || c.fiscal_code || c.email || id;
  };

  return (
    <div className="p-6" data-testid="receipts-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">🧾 Ricevute</h1>
          <p className="text-sm text-slate-600">Emetti ricevute per polizze o singolarmente. Numerazione, voci di default e testo esenzione IVA configurabili.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpenSettings(true)} data-testid="receipts-settings-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Impostazioni
          </Button>
          <Button onClick={openNew} data-testid="receipts-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova ricevuta
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Input placeholder="Cerca per numero, cognome, nome..." value={q} onChange={(e) => setQ(e.target.value)}
               className="max-w-md" data-testid="receipts-search-input"
               onKeyDown={(e) => e.key === "Enter" && load()} />
        <Button variant="outline" onClick={load}>Cerca</Button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-600">📅 Anno:</span>
          <select
            className="h-9 px-2 border border-slate-300 rounded-md text-sm bg-white"
            value={yearFilter}
            onChange={(e) => setYearFilter(parseInt(e.target.value, 10))}
            data-testid="receipts-year-filter"
            title="Filtra archivio ricevute per anno"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}{y === new Date().getFullYear() ? " (corrente)" : ""}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700 text-left">
            <tr>
              <th className="px-3 py-2">Numero</th>
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Polizza</th>
              <th className="px-3 py-2 text-right">Totale</th>
              <th className="px-3 py-2">Stato</th>
              <th className="px-3 py-2 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Caricamento…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Nessuna ricevuta emessa.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`receipt-row-${r.id}`}>
                <td className="px-3 py-2 font-mono">{r.receipt_number}</td>
                <td className="px-3 py-2">{formatDate(r.receipt_date)}</td>
                <td className="px-3 py-2">{clientLabel(r.client_id)}</td>
                <td className="px-3 py-2 text-slate-600">{r.policy_number || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(r.totale)}</td>
                <td className="px-3 py-2">
                  {r.status === "annullata"
                    ? <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-xs">Annullata</span>
                    : <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs">Emessa</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openAttachmentPreview(`/receipts/${r.id}/pdf`, { filename: `Ricevuta_${r.receipt_number || r.id}.pdf`, content_type: "application/pdf" })} data-testid={`receipt-preview-${r.id}`} title="Anteprima PDF">
                      <Eye className="w-4 h-4 text-emerald-700" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} data-testid={`receipt-pdf-${r.id}`} title="Scarica PDF">
                      <Download className="w-4 h-4" />
                    </Button>
                    {r.status !== "annullata" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openSendEmail(r)} data-testid={`receipt-send-email-${r.id}`} title="Invia via email al cliente">
                          <Mail className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openExisting(r)} data-testid={`receipt-edit-${r.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => annulla(r)} data-testid={`receipt-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {/* 🆕 Feb 2026 — Riga totali (esclude annullate) */}
          {!loading && items.length > 0 && (() => {
            const active = items.filter((r) => r.status !== "annullata");
            const totalActive = active.reduce((s, r) => s + (Number(r.totale) || 0), 0);
            const cancelled = items.length - active.length;
            return (
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="receipts-totals-row">
                  <td className="px-3 py-2 text-xs uppercase tracking-wide text-slate-600" colSpan={4}>
                    Totale {yearFilter} — {active.length} ricevuta/e emessa/e{cancelled > 0 ? ` (${cancelled} annullata/e escluse)` : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800 text-base" data-testid="receipts-grand-total">
                    {formatCurrency(totalActive)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>

      {/* Modal Nuova/Modifica */}
      {openEdit && editing && (
        <Dialog open={openEdit} onOpenChange={(v) => { if (!v) { setOpenEdit(false); setEditing(null); } }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="receipt-modal">
            <DialogHeader>
              <DialogTitle>{editing.id ? `Modifica ricevuta ${editing.receipt_number || ""}` : "Nuova ricevuta"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-slate-600">
                  Cliente *
                  <select className="w-full h-9 px-2 border border-slate-300 rounded mt-1" value={editing.client_id}
                          onChange={(e) => setEditing({ ...editing, client_id: e.target.value })}
                          data-testid="receipt-client-select">
                    <option value="">— seleziona —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.surname} {c.name} · CF {c.fiscal_code || "—"}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-slate-600">
                  Data ricevuta
                  <Input type="date" value={editing.receipt_date}
                         onChange={(e) => setEditing({ ...editing, receipt_date: e.target.value })}
                         data-testid="receipt-date-input" />
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Voci</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" type="button"
                            onClick={() => setEditing({ ...editing, lines: [...(editing.lines || []), { description: "", amount: 0 }] })}
                            data-testid="receipt-add-line">
                      + Voce
                    </Button>
                    {settings?.default_lines?.length > 0 && (
                      <Button size="sm" variant="outline" type="button"
                              onClick={() => setEditing({ ...editing, lines: settings.default_lines.map((l) => ({ ...l })) })}
                              data-testid="receipt-load-defaults">
                        📋 Voci di default
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(editing.lines || []).map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`receipt-line-${i}`}>
                      <Input className="col-span-9" placeholder="Descrizione" value={l.description}
                             onChange={(e) => {
                               const next = [...editing.lines];
                               next[i] = { ...next[i], description: e.target.value };
                               setEditing({ ...editing, lines: next });
                             }} />
                      <Input className="col-span-2 text-right" type="number" step="0.01" placeholder="0,00" value={l.amount}
                             onChange={(e) => {
                               const next = [...editing.lines];
                               next[i] = { ...next[i], amount: e.target.value };
                               setEditing({ ...editing, lines: next });
                             }} />
                      <Button size="sm" variant="ghost" className="col-span-1"
                              onClick={() => setEditing({ ...editing, lines: editing.lines.filter((_, j) => j !== i) })}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="text-xs text-slate-600">
                  Aliquota IVA %
                  <Input type="number" step="0.01" value={editing.iva_rate}
                         onChange={(e) => setEditing({ ...editing, iva_rate: e.target.value })} />
                </label>
                <label className="text-xs text-slate-600">
                  Ritenuta d&apos;acconto %
                  <Input type="number" step="0.01" value={editing.ritenuta_rate}
                         onChange={(e) => setEditing({ ...editing, ritenuta_rate: e.target.value })} />
                </label>
                <div className="flex items-end">
                  <div className="w-full bg-slate-50 border border-slate-200 rounded p-2 text-sm">
                    <div className="flex justify-between"><span>Imponibile</span><strong>{formatCurrency(totals.imponibile)}</strong></div>
                    <div className="flex justify-between text-xs text-slate-600"><span>IVA</span><span>{formatCurrency(totals.iva)}</span></div>
                    <div className="flex justify-between text-xs text-slate-600"><span>Ritenuta</span><span>-{formatCurrency(totals.rit)}</span></div>
                    <div className="flex justify-between border-t border-slate-300 mt-1 pt-1 font-bold">
                      <span>Totale</span><span data-testid="receipt-total">{formatCurrency(totals.tot)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <label className="text-xs text-slate-600 block">
                Testo esenzione IVA (mostrato sotto la tabella nel PDF)
                <Input value={editing.iva_exempt_text || ""}
                       onChange={(e) => setEditing({ ...editing, iva_exempt_text: e.target.value })}
                       placeholder="IVA ESENTE ex Art. 10, D.P.R. n° 633 del 26/10/1972" />
              </label>
              <label className="text-xs text-slate-600 block">
                Note interne (opzionali)
                <textarea className="w-full border border-slate-300 rounded p-2 text-sm mt-1" rows={2}
                          value={editing.notes || ""}
                          onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpenEdit(false); setEditing(null); }}>Annulla</Button>
              <Button onClick={save} data-testid="receipt-save-btn">💾 {editing.id ? "Aggiorna" : "Emetti ricevuta"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Impostazioni */}
      {openSettings && (
        <ReceiptSettingsDialog
          initial={settings}
          onClose={() => setOpenSettings(false)}
          onSaved={async () => { await loadSettings(); toast.success("Impostazioni salvate"); setOpenSettings(false); }}
        />
      )}

      {/* 🆕 Anteprima email prima dell'invio */}
      {emailTarget && (
        <EmailPreviewDialog
          open={emailPreviewOpen}
          onOpenChange={(v) => { setEmailPreviewOpen(v); if (!v) setEmailTarget(null); }}
          emailTypeKey="receipt_to_client"
          title="Anteprima invio ricevuta al cliente"
          fetchPreview={async () => {
            const { data } = await api.post(
              `/receipts/${emailTarget.receipt.id}/send-to-client?dry_run=true`,
              { to_email: emailTarget.toEmail },
            );
            return data;
          }}
          onConfirmSend={async () => {
            await doSendEmail(emailTarget.receipt, emailTarget.toEmail);
          }}
        />
      )}
      {/* 🆕 Anteprima inline PDF ricevuta */}
      {AttachmentPreviewDialog}
    </div>
  );
}

// 🆕 Riga sortable per drag & drop dei default_lines
function SortableLineRow({ id, index, line, onChangeDesc, onChangeAmount, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="grid grid-cols-12 gap-2 items-center bg-white" data-testid={`settings-line-row-${index}`}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="col-span-1 h-9 flex items-center justify-center rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-900"
        title="Trascina per riordinare"
        data-testid={`settings-line-drag-${index}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <Input className="col-span-8" placeholder="Descrizione" value={line.description} onChange={(e) => onChangeDesc(e.target.value)} />
      <Input className="col-span-2 text-right" type="number" step="0.01" value={line.amount} onChange={(e) => onChangeAmount(e.target.value)} />
      <Button size="sm" variant="ghost" className="col-span-1" onClick={onDelete}>
        <Trash2 className="w-4 h-4 text-rose-600" />
      </Button>
    </div>
  );
}

function ReceiptSettingsDialog({ initial, onClose, onSaved }) {
  // 🆕 Aggiungi ID stabili per dnd-kit (necessario per il tracking del drag&drop)
  const initLines = (initial?.default_lines || DEFAULT_LINES).map((l, i) => ({
    ...l,
    _id: l._id || `line-${i}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  }));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [draft, setDraft] = useState(() => ({
    number_pattern: initial?.number_pattern || "{N}/PRIV/{YYYY}",
    default_lines: initLines,
    iva_exempt_text: initial?.iva_exempt_text || "IVA ESENTE ex Art. 10, D.P.R. n° 633 del 26/10/1972",
    default_iva_rate: initial?.default_iva_rate ?? 0,
    default_ritenuta_rate: initial?.default_ritenuta_rate ?? 0,
    starting_number: initial?.starting_number ?? 1,
  }));
  const regimeActive = initial?._regime_active;
  const regimeCustomized = initial?._regime_customized;
  const resetFromRegime = async () => {
    if (!window.confirm("Ripristinare i valori consigliati dal regime fiscale? Questo sovrascriverà testo esenzione IVA, aliquota e ritenuta.")) return;
    try {
      const { data } = await api.post("/agency/receipt-settings/reset-from-regime");
      setDraft((d) => ({
        ...d,
        iva_exempt_text: data?.iva_exempt_text || "",
        default_iva_rate: data?.default_iva_rate ?? 0,
        default_ritenuta_rate: data?.default_ritenuta_rate ?? 0,
      }));
      toast.success("Valori riportati al default del regime fiscale");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async () => {
    try {
      await api.put("/agency/receipt-settings", {
        ...draft,
        default_iva_rate: Number(draft.default_iva_rate) || 0,
        default_ritenuta_rate: Number(draft.default_ritenuta_rate) || 0,
        starting_number: Number(draft.starting_number) || 1,
        default_lines: draft.default_lines.map((l) => ({ description: l.description || "", amount: Number(l.amount) || 0 })),
      });
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="receipts-settings-modal">
        <DialogHeader>
          <DialogTitle>⚙️ Impostazioni Ricevute</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {regimeActive && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex items-start justify-between gap-2" data-testid="regime-hint-banner">
              <div>
                <div className="font-semibold mb-0.5">💼 Regime fiscale attivo: <span className="uppercase">{regimeActive}</span></div>
                <div className="text-blue-700">
                  {regimeCustomized
                    ? "I valori sotto sono stati personalizzati manualmente. Clicca \"Reset dal regime\" per tornare ai default."
                    : "I valori sotto sono derivati dal regime fiscale dell'agenzia. Modificandoli manualmente, la sincronizzazione automatica verrà disattivata."}
                </div>
              </div>
              {regimeCustomized && (
                <Button size="sm" variant="outline" onClick={resetFromRegime} data-testid="reset-from-regime-btn">
                  ↺ Reset dal regime
                </Button>
              )}
            </div>
          )}
          <label className="block text-xs text-slate-600">
            Pattern numerazione (placeholder: <code className="bg-slate-100 px-1">{"{N}"}</code>, <code className="bg-slate-100 px-1">{"{N4}"}</code>, <code className="bg-slate-100 px-1">{"{YYYY}"}</code>, <code className="bg-slate-100 px-1">{"{YY}"}</code>)
            <Input value={draft.number_pattern} onChange={(e) => setDraft({ ...draft, number_pattern: e.target.value })}
                   placeholder="{N}/PRIV/{YYYY}" data-testid="settings-pattern-input" />
            <p className="text-[10px] text-slate-500 mt-0.5">Esempi: <code>{"{N}/PRIV/{YYYY}"}</code> → 95/PRIV/2026 · <code>{"RIC-{YYYY}-{N4}"}</code> → RIC-2026-0095</p>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-xs text-slate-600">
              Prossimo numero da usare
              <Input type="number" min="1" value={draft.starting_number}
                     onChange={(e) => setDraft({ ...draft, starting_number: e.target.value })} />
            </label>
            <label className="text-xs text-slate-600">
              IVA % default
              <Input type="number" step="0.01" value={draft.default_iva_rate}
                     onChange={(e) => setDraft({ ...draft, default_iva_rate: e.target.value })} />
            </label>
            <label className="text-xs text-slate-600">
              Ritenuta % default
              <Input type="number" step="0.01" value={draft.default_ritenuta_rate}
                     onChange={(e) => setDraft({ ...draft, default_ritenuta_rate: e.target.value })} />
            </label>
          </div>
          <label className="block text-xs text-slate-600">
            Testo esenzione IVA
            <Input value={draft.iva_exempt_text} onChange={(e) => setDraft({ ...draft, iva_exempt_text: e.target.value })} />
          </label>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Voci di default (auto-caricate su nuova ricevuta)</span>
              <Button size="sm" variant="outline" type="button"
                      onClick={() => setDraft({ ...draft, default_lines: [...draft.default_lines, { description: "", amount: 0, _id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }] })}>
                + Voce
              </Button>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                setDraft((d) => {
                  const oldIndex = d.default_lines.findIndex((l) => l._id === active.id);
                  const newIndex = d.default_lines.findIndex((l) => l._id === over.id);
                  if (oldIndex < 0 || newIndex < 0) return d;
                  return { ...d, default_lines: arrayMove(d.default_lines, oldIndex, newIndex) };
                });
              }}
            >
              <SortableContext items={draft.default_lines.map((l) => l._id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1.5">
                  {draft.default_lines.map((l, i) => (
                    <SortableLineRow
                      key={l._id}
                      id={l._id}
                      index={i}
                      line={l}
                      onChangeDesc={(txt) => {
                        const next = [...draft.default_lines];
                        next[i] = { ...next[i], description: txt };
                        setDraft({ ...draft, default_lines: next });
                      }}
                      onChangeAmount={(val) => {
                        const next = [...draft.default_lines];
                        next[i] = { ...next[i], amount: val };
                        setDraft({ ...draft, default_lines: next });
                      }}
                      onDelete={() => setDraft({ ...draft, default_lines: draft.default_lines.filter((_, j) => j !== i) })}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <p className="text-[10px] text-slate-500 mt-1">💡 La voce che contiene &quot;premio&quot; viene auto-compilata dal premio della polizza quando emetti una ricevuta collegata. Trascina la maniglia <GripVertical className="inline w-3 h-3" /> per riordinare le voci — l&apos;ordine viene salvato con le impostazioni.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} data-testid="settings-save-btn">💾 Salva impostazioni</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
